import Image from 'next/image'
import styled, { css, keyframes } from 'styled-components'

type PrimaryElectronAnimationProps = {
  halfOrbit: number
}

type SecondaryElectronAnimationProps = PrimaryElectronAnimationProps & {
  electronSize: number
}

const primaryElectronAnimation = ({
  halfOrbit,
}: PrimaryElectronAnimationProps) =>
  keyframes`
  0% {
    transform:
      rotateZ(0deg)
      translateX(${halfOrbit}px)
      rotateZ(0deg)
      rotateY(-60deg);
  }
  100% {
    transform:
      rotateZ(360deg)
      translateX(${halfOrbit}px)
      rotateZ(-360deg)
      rotateY(-60deg);
  }
`

const secondaryElectronAnimation = ({
  halfOrbit,
  electronSize,
}: SecondaryElectronAnimationProps) =>
  keyframes`
  0% {
    transform:
      translateY(-${electronSize}px)
      rotateZ(0deg)
      translateX(-${halfOrbit}px)
      rotateZ(0deg)
      rotateY(-60deg);
  }
  100% {
    transform:
      translateY(-${electronSize}px)
      rotateZ(360deg)
      translateX(-${halfOrbit}px)
      rotateZ(-360deg)
      rotateY(-60deg);
  }
`

const commonStyles = css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 50%;
  margin: auto;
`

export const Atom = styled.div`
  display: inline-block;
  position: relative;
  width: 100%;
  height: 100%;
  transition: all 1s ease-in;
  scale: 1; // prevent distortions
`

export const Orbit = styled.div`
  ${commonStyles}

  width: 300px;
  height: 300px;
  transform-style: preserve-3d;

  &::before {
    content: ' ';
    position: absolute;
    z-index: -1;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    border: 4px solid ${({ theme }) => theme.colors.primary_3};
    border-radius: 50%;
    filter: blur(1.25px); // fix anti-aliasing
  }

  &:nth-child(2) {
    transform: rotateY(60deg) rotateX(-54deg);
  }

  &:nth-child(3) {
    transform: rotateY(60deg) rotateX(54deg);
  }

  &:nth-child(4) {
    transform: rotateZ(90deg) rotateY(72deg);
  }

  @media (min-width: 769px) {
    width: 560px;
    height: 560px;
  }
`

export const Nucleus = styled(Image)`
  ${commonStyles}

  width: 80px;
  height: 80px;
  border-radius: 0;
  z-index: 1;

  @media (min-width: 769px) {
    width: 120px;
    height: 120px;
  }
`

export const PrimaryElectron = styled.div<{ animationTime: number }>`
  ${commonStyles}

  position: relative;
  border-radius: 50%;
  background: black;

  top: calc((300px - 20px) / 2); // (orbitSize - electronSize) / 2
  width: 20px;
  height: 20px;
  animation: ${primaryElectronAnimation({ halfOrbit: 300 / 2 })}
    ${({ animationTime }) => animationTime}s infinite linear;

  @media (min-width: 769px) {
    top: calc((560px - 40px) / 2);
    width: 40px;
    height: 40px;
    animation: ${primaryElectronAnimation({ halfOrbit: 560 / 2 })}
      ${({ animationTime }) => animationTime}s infinite linear;
  }
`

export const SecondaryElectron = styled(PrimaryElectron)`
  animation: ${secondaryElectronAnimation({
      halfOrbit: 300 / 2,
      electronSize: 20,
    })}
    ${({ animationTime }) => animationTime}s infinite linear;

  @media (min-width: 769px) {
    animation: ${secondaryElectronAnimation({
        halfOrbit: 560 / 2,
        electronSize: 40,
      })}
      ${({ animationTime }) => animationTime}s infinite linear;
  }
`
